import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { PartyService } from './party.service';

import { Party } from '@model/party.model';
import { InsuredPersonStore } from '@model/insured-person.model';

@Injectable({
  providedIn: 'root'
})
export class InsuredPersonService {
  private insuredPerson$: BehaviorSubject<InsuredPersonStore[]> = new BehaviorSubject([]);
  private insuredPersonFromSearch$: BehaviorSubject<InsuredPersonStore[]> = new BehaviorSubject([]);

  private insuredPersonDataForBusinessUnit$: BehaviorSubject<Party> = new BehaviorSubject(null);
  private insuredPersonFromSearchFirstIndex$: BehaviorSubject<Party> = new BehaviorSubject(null);

  constructor(private partyBuilder: PartyService) {}

  getInsuredPersonFromSearchFirstIndex(): Observable<Party> {
    return this.insuredPersonFromSearchFirstIndex$.asObservable();
  }

  setInsuredPersonFromSearchFirstIndex(party: Party): void {
    this.insuredPersonFromSearchFirstIndex$.next(party);
  }

  getInsuredPersonDataForBusinessUnit(): Observable<Party> {
    return this.insuredPersonDataForBusinessUnit$.pipe(filter((data: Party) => data !== null));
  }

  setInsuredPersonDataForBusinessUnit(data: Party): void {
    this.insuredPersonDataForBusinessUnit$.next(data);
  }

  clearInsuredPersonDataForBusinessUnit(): void {
    this.insuredPersonDataForBusinessUnit$.next(this.partyBuilder.personPartyBuilder());
  }

  getInsuredPerson(): Observable<InsuredPersonStore[]> {
    return this.insuredPerson$.asObservable();
  }

  getInsuredPersonByIndex(index: number): Observable<InsuredPersonStore> {
    return this.insuredPerson$.pipe(
      map((store: InsuredPersonStore[]) => {
        if (store.length === 0) {
          return null;
        }

        const result: InsuredPersonStore[] = store.filter((item: InsuredPersonStore) => item.index === index);

        if (result.length !== 0) {
          return result[0];
        } else {
          return null;
        }
      })
    );
  }

  getInsuredPersonFromSearch(): Observable<InsuredPersonStore[]> {
    return this.insuredPersonFromSearch$.asObservable();
  }

  getInsuredPersonFromSearchByIndex(index: number): Observable<InsuredPersonStore> {
    return this.insuredPersonFromSearch$.pipe(
      filter((store: InsuredPersonStore[]) => store.length > 0),
      map((store: InsuredPersonStore[]) => {
        const result: InsuredPersonStore[] = store.filter((item: InsuredPersonStore) => item.index === index);

        if (result.length !== 0) {
          return result[0];
        } else {
          return null;
        }
      }),
      filter((store: InsuredPersonStore) => store !== null)
    );
  }

  setInsuredPersonByIndex(index: number, party: Party): void {
    this.insuredPerson$.next([
      ...this.insuredPerson$.getValue().filter((value: InsuredPersonStore) => {
        return value.index !== index;
      }),
      { index, data: party }
    ]);
  }

  setInsuredPersonFromSearchByIndex(index: number, party: Party): void {
    this.insuredPersonFromSearch$.next([
      ...this.insuredPersonFromSearch$.getValue().filter((value: InsuredPersonStore) => {
        return value.index !== index;
      }),
      { index, data: party }
    ]);
  }

  clearInsuredPerson(): void {
    this.insuredPerson$.next([]);
  }

  clearInsuredPersonFromSearch(): void {
    this.insuredPersonFromSearch$.next([]);
  }

  checkPartyDuplication(index: number, nationalId: number, passportNumber: string): Observable<boolean> {
    return this.getInsuredPerson().pipe(
      map((insuredPersonList: InsuredPersonStore[]) => {
        const duplicatedNationalIdParty: InsuredPersonStore[] = insuredPersonList
          .filter((store: InsuredPersonStore) => store.index !== index)
          .filter(({ data: { person } }: InsuredPersonStore) => (nationalId === null ? true : person.nationalId === nationalId))
          .filter(({ data: { person } }: InsuredPersonStore) =>
            passportNumber === null ? true : person.passportNumber === passportNumber.toUpperCase()
          );
        return duplicatedNationalIdParty.length > 0;
      })
    );
  }

  getPartyDuplicationByFormValue(index: number, passportNumber: string): InsuredPersonStore[] {
    return this.insuredPerson$
      .getValue()
      .filter((store: InsuredPersonStore) => store.index !== index)
      .filter(({ data: { person } }: InsuredPersonStore) =>
        passportNumber === null ? true : person.passportNumber === passportNumber.toUpperCase()
      );
  }
}
